import React, { FC, useCallback, useEffect } from 'react';

import { Stack } from '@mui/system';

import { dropdownMapper } from '@pharmaplan/common/helpers/utils';
import {
  adminResetValue,
  getPharmaciesInfo,
  IAdminApproveDetailsData,
  IAdminRequestDetailsContractModel,
  ICalendarEventType,
} from '@pharmaplan/common';

import AdminLongTermHourlyRates from '../PharmacyList/AdminCreateWorkshift/AdminLongTermHourlyRates';
import LongTermHourlyRates from '../../SelfService/Workshifts/AddWorkshift/LongTermHourlyRates';
import CustomDropdown from '../../common/CustomDropdown';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import CustomTextField from '../../common/CustomTextField';
import strings from '../../../localization';
import genericClasses from '../../../theme/GenericClasses';
import CustomCheckbox from '../../common/CustomCheckbox';
import {
  ICustomBack,
  IFromScreen,
  setBack,
  setFooter,
  setFooterShow,
} from '../../../actions/drawerActions';
import useApproveBooking from '../../../hooks/ApproveBooking/useApproveBooking';
import AllowanceWorkshift from '../../SelfService/Workshifts/AllowanceWorkshift';
import useSelfService from '../../../hooks/useSelfService';
import { Constants } from '../../../helpers/Constants';

import styles from './styles';
import { config } from './helper';
import DetailsHeader from './DetailsHeader';

const { priceSuffix, allowancePlaceholder } = Constants;
export interface IApproveBooking {
  workshiftId: string;
  bookingId: string;
  eventIds?: Array<string>;
  fromScreen?: IFromScreen;
  type?: ICalendarEventType;
  back?: ICustomBack;
  isAutoSyncRecord?: boolean;
  longTermShiftContractId?: string;
  professionalId?: string;
}

const ApproveBooking: FC<IApproveBooking> = ({
  bookingId,
  workshiftId,
  eventIds,
  type,
  fromScreen,
  back,
  professionalId,
  isAutoSyncRecord,
  longTermShiftContractId,
}) => {
  const dispatch = useAppDispatch();

  const { isSelfService } = useSelfService();
  const isFullSyncedEvent = !isSelfService || isAutoSyncRecord;
  const LongTermRateComponent = isSelfService ? LongTermHourlyRates : AdminLongTermHourlyRates;

  const { details, formik } = useApproveBooking({
    fromScreen,
    eventIds,
    bookingId,
    workshiftId,
    type,
    back,
    isAutoSyncRecord,
    longTermShiftContractId,
    professionalId,
  });

  const {
    pharmacyName,
    startDate,
    endDate,
    professionalName,
    pharmacistName,
    pharmacyId,
    counterOfferId,
  } = (details as IAdminApproveDetailsData) ?? {};

  const { totalWeekHours, totalWeekendHours, requests } = details as IAdminRequestDetailsContractModel;
  const userName = isSelfService || longTermShiftContractId
    ? professionalName
    : pharmacistName;
  const { handleSubmit } = formik ?? {};

  useEffect(() => {
    dispatch(
      setFooter({
        show: true,
        pagination: {
          show: false,
        },
        buttons: {
          show: true,
          primaryButton: {
            label: strings.approve,
            onClick: handleSubmit,
          },
        },
      }),
    );

    return () => {
      dispatch(setFooterShow(false));
      dispatch(setBack(null));
      dispatch(adminResetValue(['adminApproveData']));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pharmacyId) {
      dispatch(getPharmaciesInfo({ id: pharmacyId, isAutoSyncRecord }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoSyncRecord, isFullSyncedEvent, pharmacyId]);

  const renderHourlyRates = useCallback(
    () =>
      config(!isFullSyncedEvent).map((item) => {
        const { key, name, label } = item ?? {};
        return (
          <CustomTextField
            label={label}
            suffix={priceSuffix}
            customClass={styles.horizontalField}
            placeholder={allowancePlaceholder}
            horizontal
            id={name}
            formik={formik}
            name={name}
            key={key}
          />
        );
      }),
    [formik, isFullSyncedEvent],
  );

  return (
    <Stack sx={styles.container}>
      <DetailsHeader
        isCounterOffer={!!counterOfferId}
        pharmacistName={userName}
        pharmacyName={pharmacyName}
        startDate={startDate}
        endDate={endDate}
        showViewAllRequests={!!(longTermShiftContractId && requests?.length >= 1)}
      />

      {isFullSyncedEvent && (
        <CustomTextField
          variant="outlined"
          formik={formik}
          id="notesForSalesOrder"
          placeholder=" "
          label={strings.notesForSalesOrder}
          name="notesForSalesOrder"
          multiline
          maxLength={200}
          customClass={genericClasses.borderNoneTextField}
        />
      )}

      {longTermShiftContractId ? (
        <LongTermRateComponent
          customWeekHours={totalWeekHours}
          customWeekendHours={totalWeekendHours}
          formik={formik}
          CustomContent={(
            <CustomTextField
              variant="outlined"
              formik={formik}
              id="allowanceNotes"
              placeholder=" "
              label={strings.allowances}
              name="allowanceNotes"
              multiline
              maxLength={200}
              customClass={genericClasses.borderNoneTextField}
            />
          )}
        />
      ) : (
        <AllowanceWorkshift
          isAdmin
          formik={formik}
          pharmacyId={pharmacyId}
          renderHourlyRate={renderHourlyRates()}
          useApi={!isFullSyncedEvent}
        />
      )}

      <CustomCheckbox
        id="1"
        handleChange={(e) =>
          formik.setFieldValue('sendEmailToPharmacist', e.target.checked)}
        value={formik.values.sendEmailToPharmacist}
        name="sendEmailToPharmacist"
        labelClass={styles.checkboxLabel}
        label={
          strings.formatString(
            strings.sendEmailNotification,
            strings.pharmacists,
          ) as string
        }
      />

      <CustomCheckbox
        value={formik.values.sendEmailToPharmacy}
        labelClass={styles.checkboxLabel}
        customClass={styles.checkbox}
        handleChange={(e) => {
          formik.setFieldValue('sendEmailToPharmacy', e.target.checked);
        }}
        id="2"
        name="sendEmailToPharmacy"
        label={
          strings.formatString(
            strings.sendEmailNotification,
            strings.pharmacies,
          ) as string
        }
      />
    </Stack>
  );
};

export default ApproveBooking;
